const MAX_FILE_BYTES = 20 * 1024 * 1024;
const MAX_IMAGE_PIXELS = 20_000_000;
const ALLOWED_IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const MAX_WIDTH_FEET = 50;
const MAX_HEIGHT_FEET = 20;

function finiteNumber(value, name) {
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new TypeError(`${name} must be a finite number`);
  return value;
}

function quarterInches(feet, inches, name, maximumFeet) {
  finiteNumber(feet, `${name} feet`);
  finiteNumber(inches, `${name} inches`);
  if (!Number.isInteger(feet) || feet < 0 || inches < 0 || inches > 11.75 || !Number.isInteger(inches * 4)) {
    throw new RangeError(`${name} must use whole feet and quarter-inch increments`);
  }

  const quarters = feet * 48 + inches * 4;
  if (quarters < 48 || quarters > maximumFeet * 48) {
    throw new RangeError(`${name} must be between 1 and ${maximumFeet} feet`);
  }
  return quarters;
}

export function calculateWallpaper({ widthFeet, widthInches, heightFeet, heightInches, unitPrice }) {
  const widthQ = quarterInches(widthFeet, widthInches, 'width', MAX_WIDTH_FEET);
  const heightQ = quarterInches(heightFeet, heightInches, 'height', MAX_HEIGHT_FEET);
  finiteNumber(unitPrice, 'unit price');
  if (!Number.isInteger(unitPrice) || unitPrice <= 0) throw new RangeError('unit price must be positive integer paise');

  const measuredArea = (widthQ * heightQ) / 2304;
  const billedUnits = Math.max(40, Math.ceil(measuredArea));
  return { measuredArea, billedUnits, total: billedUnits * unitPrice };
}

export function formatMeasuredArea(area) {
  finiteNumber(area, 'measured area');
  return (Math.ceil(area * 100) / 100).toFixed(2);
}

/**
 * Reads a typed feet/inches pair into whole feet plus exact quarter-inches.
 *
 * calculateWallpaper is deliberately strict — it is the money math — but people
 * type "10.5" in the feet box meaning 10 ft 6 in, type "12" in the inches box
 * meaning one more foot, and type inches the studio cannot bill to (3.3).
 * Every one of those used to throw, and #updateWallpaper blanked the whole
 * summary with no reason shown, so the calculator read as dead. Normalise the
 * input here and let the strict function keep its contract.
 *
 * Returns null when the pair is not a pair of numbers at all.
 */
export function normalizeDimension(feet, inches) {
  if (!Number.isFinite(feet) || !Number.isFinite(inches) || feet < 0 || inches < 0) return null;

  const quarters = Math.round((feet * 12 + inches) * 4);
  return { feet: Math.floor(quarters / 48), inches: (quarters % 48) / 4 };
}

/**
 * The customer-facing reason a side cannot be billed, or null when it can.
 * `side` is used inside the sentence, so it stays lowercase.
 */
export function dimensionProblem(side, normalized, maximumFeet) {
  if (!normalized) return `Enter the ${side} in feet and inches.`;

  const quarters = normalized.feet * 48 + normalized.inches * 4;
  if (quarters < 48) return `The ${side} must be at least 1 ft.`;
  if (quarters > maximumFeet * 48) {
    return `The ${side} can be up to ${maximumFeet} ft. For a larger wall, speak to our art advisor.`;
  }
  return null;
}

function printPixels(size, ppi) {
  finiteNumber(ppi, 'PPI');
  if (!Number.isInteger(ppi) || ppi <= 0) throw new RangeError('PPI must be a positive integer');
  const match = /^\s*(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:in|inch|inches|")\s*$/i.exec(size);
  if (!match) throw new RangeError('print size must be expressed in inches');

  const dimensions = [Number(match[1]), Number(match[2])].sort((a, b) => a - b);
  if (dimensions.some((value) => !Number.isFinite(value) || value <= 0)) throw new RangeError('invalid print size');
  return { short: Math.ceil(dimensions[0] * ppi), long: Math.ceil(dimensions[1] * ppi) };
}

export function requiredPixels(size, ppi) {
  const pixels = printPixels(size, ppi);
  if (pixels.short * pixels.long <= MAX_IMAGE_PIXELS) return pixels;
  const scale = Math.sqrt(MAX_IMAGE_PIXELS / (pixels.short * pixels.long));
  return { short: Math.floor(pixels.short * scale), long: Math.floor(pixels.long * scale) };
}

export function usesMaximumUploadResolution(size, ppi) {
  const pixels = printPixels(size, ppi);
  return pixels.short * pixels.long > MAX_IMAGE_PIXELS;
}

export function validateImageMetadata(file, required) {
  if (!ALLOWED_IMAGE_TYPES.has(file?.type)) return 'type';
  if (!Number.isFinite(file.size) || file.size < 0 || file.size > MAX_FILE_BYTES) return 'size';
  if (!Number.isFinite(file.width) || !Number.isFinite(file.height) || file.width <= 0 || file.height <= 0) {
    return 'dimensions';
  }
  if (file.width * file.height > MAX_IMAGE_PIXELS) return 'pixels';

  const dimensions = [file.width, file.height].sort((a, b) => a - b);
  if (dimensions[0] < required.short || dimensions[1] < required.long) return 'dimensions';
  return null;
}

export function resolveWallpaperVariant(variants) {
  return (
    variants.find((variant) => {
      const options = Array.isArray(variant.options) ? variant.options : [];
      return options.some((value) => String(value).trim().toLowerCase() === 'per sq ft.');
    }) ?? null
  );
}

export function formatDimension(feet, inches) {
  finiteNumber(feet, 'feet');
  finiteNumber(inches, 'inches');
  if (!Number.isInteger(feet) || feet < 0 || inches < 0 || inches > 11.75 || !Number.isInteger(inches * 4)) {
    throw new RangeError('dimension must use whole feet and quarter-inch increments');
  }

  const wholeInches = Math.floor(inches);
  const fraction = ['', '¼', '½', '¾'][Math.round((inches - wholeInches) * 4)];
  const inchText = `${wholeInches || ''}${fraction}`;
  return [feet > 0 ? `${feet} ft` : '', inchText ? `${inchText} in` : ''].filter(Boolean).join(' ');
}

export function createConfigurationId() {
  if (typeof globalThis.crypto?.randomUUID !== 'function') throw new Error('Secure configuration IDs are unavailable');
  return globalThis.crypto.randomUUID();
}

const PREPARATION_ERROR_MESSAGE =
  'We could not prepare your configuration. Please try again or contact our art advisor.';

export function prepareConfiguredSubmission({ revalidate, isReady, canonicalize, createId, assignId, fail }) {
  try {
    revalidate();
    if (!isReady()) return false;
    canonicalize();
    const id = createId();
    if (typeof id !== 'string' || id.trim() === '') throw new Error('Configuration ID is unavailable');
    assignId(id);
    return true;
  } catch {
    fail(PREPARATION_ERROR_MESSAGE);
    return false;
  }
}

export function countCartItems(items) {
  if (!Array.isArray(items)) return 0;
  return items.reduce(
    (count, item) => count + (item?.properties?._sa_kind === 'wallpaper-v1' ? 1 : Number(item?.quantity) || 0),
    0
  );
}

function decodeImage(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('The image could not be read'));
    };
    image.src = url;
  });
}

const IMAGE_ERRORS = {
  type: 'Use a JPEG, PNG or WebP image.',
  size: 'Each image must be 20 MB or smaller.',
  pixels: 'Each image must be 20 megapixels or smaller.',
  dimensions: 'This image is too small for the required print size.',
};

const HTMLElementBase = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

class SAConfiguredProduct extends HTMLElementBase {
  #form;
  #formComponent;
  #variants = [];
  #selectedVariant;
  #variantListenerTarget;
  #previewUrls = new Map();

  connectedCallback() {
    this.#form = document.getElementById(this.dataset.formId);
    this.#formComponent = this.#form?.closest('product-form-component');
    if (!this.#form || !this.#formComponent) return;
    try {
      this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      this.#setReady(false);
      return;
    }

    this.addEventListener('input', this.#onInput);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onConfiguredAction);
    this.#variantListenerTarget = this.closest('.shopify-section');
    this.#variantListenerTarget?.addEventListener('shopify:product:select', this.#onVariantSelect);

    if (this.dataset.family === 'wallpaper') this.#updateWallpaper();
    else this.#updateFrameRequirements();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onConfiguredAction);
    this.#variantListenerTarget?.removeEventListener('shopify:product:select', this.#onVariantSelect);
    for (const url of this.#previewUrls.values()) URL.revokeObjectURL(url);
  }

  #onInput = (event) => {
    if (this.dataset.family === 'wallpaper' && event.target.matches('[data-wall-dimension]')) {
      this.#updateWallpaper();
    }
  };

  #onChange = (event) => {
    if (event.target.matches('input[type="file"]')) this.#validateFile(event.target);
    else if (this.dataset.family === 'wallpaper' && event.target.matches('[data-wall-dimension]')) {
      // Only once the field is committed, so writing "10 ft 6 in" back never
      // fights someone still part-way through typing "10.5".
      this.#updateWallpaper({ writeBack: true });
    } else if (this.dataset.family === 'frame') this.#updateFrameReady();
  };

  #onConfiguredAction = (event) => {
    const action = event.target.closest?.('[data-sa-configured-action]');
    if (!action || action.disabled || this.#formComponent.dataset.configurationReady !== 'true') return;
    const nativeButton = this.#nativeButton();
    if (!nativeButton || nativeButton.disabled) {
      action.disabled = true;
      return;
    }
    nativeButton.click();
    action.disabled = nativeButton.disabled;
  };

  #nativeButton() {
    return this.#formComponent?.querySelector('[ref="addToCartButton"]');
  }

  #onVariantSelect = (event) => {
    if (this.dataset.family !== 'frame') return;
    this.#setReady(false);
    event.promise
      ?.then(({ detail }) => {
        if (detail?.productId && String(detail.productId) !== String(this.#formComponent.dataset.productId)) return;
        this.#selectedVariant = detail?.resource ?? null;
        this.#updateFrameRequirements();
      })
      .catch(() => this.#setReady(false));
  };

  #variant() {
    if (this.#selectedVariant) return this.#selectedVariant;
    const id = this.#form.querySelector('[name="id"]')?.value;
    return this.#variants.find((variant) => String(variant.id) === String(id)) ?? null;
  }

  #money(paise) {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: this.dataset.currency }).format(paise / 100);
  }

  #input(name) {
    return this.querySelector(`[data-field="${name}"]`);
  }

  /** True until the customer has put a number in any of the four boxes. */
  #wallUntouched() {
    return ['width-feet', 'width-inches', 'height-feet', 'height-inches'].every((name) => {
      const value = this.#input(name)?.value ?? '';
      return value === '' || Number(value) === 0;
    });
  }

  #showNormalized(feetField, inchesField, normalized) {
    if (!normalized) return;
    const feet = this.#input(feetField);
    const inches = this.#input(inchesField);
    if (feet && String(normalized.feet) !== feet.value) feet.value = String(normalized.feet);
    if (inches && String(normalized.inches) !== inches.value) inches.value = String(normalized.inches);
  }

  #updateWallpaper({ writeBack = false } = {}) {
    const variant = resolveWallpaperVariant(this.#variants);
    let message = '';

    try {
      if (!variant || variant.available === false) {
        throw new Error('This wallpaper cannot be configured right now. Please contact our art advisor.');
      }

      const width = normalizeDimension(
        Number(this.#input('width-feet').value),
        Number(this.#input('width-inches').value)
      );
      const height = normalizeDimension(
        Number(this.#input('height-feet').value),
        Number(this.#input('height-inches').value)
      );

      if (writeBack) {
        this.#showNormalized('width-feet', 'width-inches', width);
        this.#showNormalized('height-feet', 'height-inches', height);
      }

      const problem =
        dimensionProblem('width', width, MAX_WIDTH_FEET) ?? dimensionProblem('height', height, MAX_HEIGHT_FEET);
      if (problem) throw new Error(this.#wallUntouched() ? '' : problem);

      const result = calculateWallpaper({
        widthFeet: width.feet,
        widthInches: width.inches,
        heightFeet: height.feet,
        heightInches: height.inches,
        unitPrice: Number(variant.price),
      });
      const measuredArea = formatMeasuredArea(result.measuredArea);

      this.querySelector('[data-measured-area]').textContent = `${measuredArea} sq ft`;
      this.querySelector('[data-billed-area]').textContent = `${result.billedUnits} sq ft`;
      this.querySelector('[data-wallpaper-total]').textContent = this.#money(result.total);
      this.dataset.configuredPrice = this.#money(result.total);
      this.querySelector('[data-minimum-note]').hidden = result.measuredArea >= 40;
      this.#input('wall-width').value = formatDimension(width.feet, width.inches);
      this.#input('wall-height').value = formatDimension(height.feet, height.inches);
      this.#input('measured-area').value = `${measuredArea} sq ft`;
      this.#input('billed-area').value = `${result.billedUnits} sq ft`;
      this.#input('rate').value = `${this.#money(variant.price)} per sq ft`;
      this.#input('quantity').value = String(result.billedUnits);
      this.#form.querySelector('[name="id"]').value = String(variant.id);
      this.#setReady(true);
      return;
    } catch (error) {
      message = error instanceof Error ? error.message : '';
    }

    this.querySelector('[data-measured-area]').textContent = '—';
    this.querySelector('[data-billed-area]').textContent = '—';
    this.querySelector('[data-wallpaper-total]').textContent = '—';
    this.dataset.configuredPrice = '';
    this.#setReady(false, message);
  }

  #selectedRequirement() {
    const variant = this.#variant();
    for (const option of variant?.options ?? []) {
      try {
        return {
          label: option,
          pixels: requiredPixels(option, 150),
          capped: usesMaximumUploadResolution(option, 150),
        };
      } catch {
        // Keep looking for the size option.
      }
    }
    return null;
  }

  #requirement(input) {
    const size = input.dataset.printSize;
    if (size === 'wall') return { short: 1, long: 1 };
    if (size && size !== 'selected') return requiredPixels(size, 150);
    return this.#selectedRequirement()?.pixels ?? null;
  }

  #updateFrameRequirements() {
    const selected = this.#selectedRequirement();
    const requirement = this.querySelector('[data-requirement]');
    if (requirement) {
      requirement.textContent = selected
        ? `For ${selected.label}, each print image must be at least ${selected.pixels.short} × ${selected.pixels.long} pixels. ${
            selected.capped
              ? "Larger formats use Shopify's maximum direct-upload resolution and receive studio pre-production review."
              : 'This is 150 PPI.'
          }`
        : 'Choose a print size to see the minimum image resolution (150 PPI).';
    }

    for (const input of this.querySelectorAll('input[type="file"]')) this.#revalidateFile(input);
    this.#updateFrameReady();
  }

  async #validateFile(input) {
    const file = input.files?.[0];
    input.dataset.validFile = 'false';
    input.setCustomValidity('Please select an image.');
    this.#clearPreview(input);
    if (!file) {
      this.#showFileError(input, '');
      this.#updateFrameReady();
      return;
    }

    try {
      const dimensions = await decodeImage(file);
      if (input.files?.[0] !== file) return;
      input.dataset.width = String(dimensions.width);
      input.dataset.height = String(dimensions.height);
      this.#revalidateFile(input);
      if (input.dataset.validFile === 'true') this.#showPreview(input, file);
    } catch (error) {
      input.setCustomValidity(error.message);
      this.#showFileError(input, error.message);
    }
    this.#updateFrameReady();
  }

  #revalidateFile(input) {
    const file = input.files?.[0];
    const requirement = this.#requirement(input);
    if (!file || !requirement) {
      input.dataset.validFile = 'false';
      input.setCustomValidity(requirement ? 'Please select an image.' : 'Choose a print size first.');
      return;
    }

    const error = validateImageMetadata(
      { type: file.type, size: file.size, width: Number(input.dataset.width), height: Number(input.dataset.height) },
      requirement
    );
    input.dataset.validFile = error ? 'false' : 'true';
    input.setCustomValidity(error ? IMAGE_ERRORS[error] : '');
    this.#showFileError(input, error ? IMAGE_ERRORS[error] : '');
  }

  #showFileError(input, message) {
    const error = this.querySelector(`[data-file-error="${input.id}"]`);
    if (error) error.textContent = message;
  }

  #clearPreview(input) {
    const oldUrl = this.#previewUrls.get(input);
    if (oldUrl) URL.revokeObjectURL(oldUrl);
    this.#previewUrls.delete(input);
    const preview = this.querySelector(`[data-file-preview="${input.id}"]`);
    if (preview) {
      preview.hidden = true;
      preview.removeAttribute('src');
    }
  }

  #showPreview(input, file) {
    const preview = this.querySelector(`[data-file-preview="${input.id}"]`);
    if (!preview) return;
    const url = URL.createObjectURL(file);
    this.#previewUrls.set(input, url);
    preview.src = url;
    preview.hidden = false;
  }

  #updateFrameReady() {
    const variant = this.#variant();
    const files = [...this.querySelectorAll('input[type="file"]')];
    const consent = this.querySelector('[data-photo-consent]');
    this.#setReady(
      Boolean(variant) &&
        variant.available !== false &&
        files.length > 0 &&
        files.every((input) => input.dataset.validFile === 'true') &&
        consent?.checked === true
    );
  }

  #setReady(ready, failureMessage = '') {
    this.#formComponent.dataset.configurationReady = ready ? 'true' : 'false';
    const button = this.#nativeButton();
    if (button) button.disabled = !ready;
    const variant = this.#variant();
    const configuredPrice = ready
      ? this.dataset.configuredPrice || (variant?.price ? this.#money(Number(variant.price)) : '')
      : '';
    for (const label of this.#formComponent.querySelectorAll('[data-sa-action-label]')) {
      label.textContent = ready ? 'Add to bag' : 'Complete configuration';
    }
    for (const price of this.#formComponent.querySelectorAll('[data-sa-action-price]')) {
      price.textContent = configuredPrice;
    }
    const action = this.querySelector('[data-sa-configured-action]');
    if (action) {
      action.disabled = !ready;
      action.textContent = ready ? 'Add to bag' : 'Complete configuration';
    }
    const actionPrice = this.querySelector('[data-sa-configured-action-price]');
    if (actionPrice) {
      actionPrice.textContent = ready ? configuredPrice || 'Ready to add' : 'Complete the fields above';
    }
    const status = this.querySelector('[data-configuration-status]');
    if (status) {
      status.textContent = ready
        ? 'Configuration complete. Ready to add.'
        : failureMessage || 'Complete every required field to continue.';
    }
  }

  prepareForSubmit() {
    return prepareConfiguredSubmission({
      revalidate: () => {
        if (this.dataset.family === 'wallpaper') this.#updateWallpaper({ writeBack: true });
        else if (this.dataset.family === 'frame') this.#updateFrameRequirements();
        else throw new Error('Unknown configured-product family');
      },
      isReady: () => this.#formComponent.dataset.configurationReady === 'true',
      canonicalize: () => {
        if (this.dataset.family !== 'wallpaper') return;
        const variant = resolveWallpaperVariant(this.#variants);
        const variantInput = this.#form.querySelector('[name="id"]');
        if (!variant || !variantInput) throw new Error('Canonical wallpaper variant is unavailable');
        variantInput.value = String(variant.id);
      },
      createId: createConfigurationId,
      assignId: (id) => {
        const input = this.#input('configuration-id');
        if (!input) throw new Error('Configuration ID field is unavailable');
        input.value = id;
      },
      fail: (message) => this.#setReady(false, message),
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get('sa-configured-product')) {
  customElements.define('sa-configured-product', SAConfiguredProduct);
}
